import { ReactQuery } from 'cms-api'
import { useRouter } from 'next/router'
import React from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled, { css } from 'styled-components'
import { AddToFavorites } from 'components/AddToFavorites'
import { AddToFavoritesDetailHintPopover } from 'components/AddToFavorites/AddToFavoritesDetailHintPopover'
import { Flex } from 'components/Layout'
import Link from 'components/Link'
import { H3, Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import {
  PRICE_UPON_REQUEST,
  formatPriceWithQuantityUnit,
  getDiscountBadge,
} from 'utils/formatters'

interface TitleProps {
  id: number
  brand?: PublicProductQuery['product']['brand']
  title: string
  showroom?: {
    name: string
    url: string
  }
  price: PublicProductQuery['product']['variants'][0]['price']
  tradePrice: PublicProductQuery['product']['variants'][0]['tradePrice']
  quantityUnit?: {
    singular: string
    plural: string
  }
  isPriceVisible: boolean
  discount?: string
}

const StyledPrice = styled(Text)`
  ${({ shouldDisplayTradePrice }) =>
    shouldDisplayTradePrice &&
    css`
      text-decoration: line-through;
    `}
`

export const Title: React.FC<React.PWC<TitleProps>> = ({
  id,
  brand,
  title,
  showroom,
  price,
  tradePrice,
  quantityUnit,
  isPriceVisible,
  discount,
}) => {
  const { user, isClientSignedIn, isExpertSignedIn } = useUserAuth()
  const router = useRouter()

  const {
    data: favoriteUserProductVariant,
    refetch: refetchFavoriteUserProductVariant,
  } = ReactQuery.useGetFavoriteUserProductVariantBookmarkLegacyProductVariantId(
    id.toString(),
    {
      query: {
        enabled: isClientSignedIn || isExpertSignedIn,
      },
    }
  )

  const formattedPrice =
    price?.total > 0
      ? formatPriceWithQuantityUnit({ price, quantityUnit })
      : PRICE_UPON_REQUEST
  const formattedTradePrice =
    tradePrice?.total > 0
      ? formatPriceWithQuantityUnit({ price: tradePrice, quantityUnit })
      : PRICE_UPON_REQUEST

  const isTradePriceAvailable =
    formattedTradePrice !== PRICE_UPON_REQUEST &&
    formattedPrice !== formattedTradePrice
  const shouldDisplayTradePrice = user?.isTradeCapable && isTradePriceAvailable

  const discountBadge = getDiscountBadge({
    price: price?.total ?? 0,
    tradePrice: tradePrice?.total ?? 0,
  })

  const isBookmarked = !!favoriteUserProductVariant?.data?.isBookmarked

  return (
    <Flex flexDirection="column">
      {brand && (
        <Link
          data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_BRAND}
          mb={SPACE.PX_4}
          fontFamily={FONT_STACK.SERIF}
          fontWeight={FONT_WEIGHT.MEDIUM}
          fontSize={FONT_SIZE.PX_13}
          href={`/${brand?.cmsBrandPage.cmsPage.slug}`}
        >
          {brand?.id}
        </Link>
      )}
      <Flex justifyContent="space-between" gap={SPACE.PX_20}>
        <H3
          data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_TITLE}
          mb={SPACE.PX_4}
        >
          {title}
        </H3>

        <AddToFavoritesDetailHintPopover>
          <AddToFavorites
            variantId={id}
            isBookmarked={isBookmarked}
            productTitle={title}
            onRefetch={refetchFavoriteUserProductVariant}
          />
        </AddToFavoritesDetailHintPopover>
      </Flex>
      {showroom && (
        <Text fontFamily={FONT_STACK.SERIF} fontWeight={FONT_WEIGHT.MEDIUM}>
          featured in{' '}
          <Link
            href={ROUTE.SHOWROOM_DETAIL({ slug: showroom.url })}
            aria-label={`showroom detail ${showroom.name}`}
          >
            {showroom.name}
          </Link>
        </Text>
      )}
      {isPriceVisible && (
        <>
          {shouldDisplayTradePrice && (
            <>
              <Text
                fontSize={FONT_SIZE.PX_16}
                color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                mt={SPACE.PX_16}
                fontWeight={FONT_WEIGHT.MEDIUM}
              >
                Trade: {formattedTradePrice}{' '}
              </Text>
              {discountBadge > 0 && (
                <Text
                  fontStyle="italic"
                  color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                  mt={SPACE.PX_4}
                  fontWeight={FONT_WEIGHT.NORMAL}
                >{`${discountBadge}% off`}</Text>
              )}
            </>
          )}
          {!user ? (
            <Flex alignItems="center" gap={SPACE.PX_20}>
              <StyledPrice
                data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_PRICE}
                fontSize={
                  shouldDisplayTradePrice ? FONT_SIZE.PX_14 : FONT_SIZE.PX_16
                }
                color={COLOR_INTENT.PRODUCT_HEADER.PRICE}
                mt={shouldDisplayTradePrice ? SPACE.PX_4 : SPACE.PX_16}
                mb={SPACE.PX_16}
                shouldDisplayTradePrice={shouldDisplayTradePrice}
              >
                {shouldDisplayTradePrice
                  ? `Retail: ${formattedPrice}`
                  : formattedPrice}
              </StyledPrice>{' '}
              <Text>
                <Link href={ROUTE.TRADE_SIGN_IN({ returnUrl: router.asPath })}>
                  <Text style={{ textDecoration: 'underline' }}>Log in</Text>
                </Link>{' '}
                for trade pricing
              </Text>
            </Flex>
          ) : (
            <StyledPrice
              data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_PRICE}
              fontSize={
                shouldDisplayTradePrice ? FONT_SIZE.PX_14 : FONT_SIZE.PX_16
              }
              color={COLOR_INTENT.PRODUCT_HEADER.PRICE}
              mt={shouldDisplayTradePrice ? SPACE.PX_4 : SPACE.PX_16}
              mb={SPACE.PX_12}
              shouldDisplayTradePrice={shouldDisplayTradePrice}
            >
              {shouldDisplayTradePrice
                ? `Retail: ${formattedPrice}`
                : formattedPrice}
            </StyledPrice>
          )}
          {shouldDisplayTradePrice && brand?.id !== 'Apparatus' && (
            <Link
              fontWeight={FONT_WEIGHT.NORMAL}
              mb={SPACE.PX_12}
              href={ROUTE.PRICE_MATCH_PROMISE_TERMS_AND_CONDITIONS()}
              target="_blank"
            >
              <Text
                color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontSize={FONT_SIZE.PX_14}
              >
                Price Match Promise
              </Text>{' '}
            </Link>
          )}
        </>
      )}
      {discount && (
        <Text
          fontSize={FONT_SIZE.PX_16}
          color={COLOR_INTENT.PRODUCT_HEADER.DISCOUNT}
          mb={SPACE.PX_16}
          fontWeight={FONT_WEIGHT.MEDIUM}
        >
          {discount}
        </Text>
      )}
    </Flex>
  )
}
